import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Grid, Theme, Typography } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import { Alert } from '@material-ui/lab';
import { createStyles, makeStyles } from '@material-ui/styles';
import { SummaryStatus } from 'api/file/interfaces/get-file-order-insights';
import React from 'react';
import { formatDate } from 'utilities/dateFormatter';
import BasicChip from './BasicChip';
type SummaryInfoProps = {
  summary: SummaryStatus;
  isPassport: boolean;
  timezone?: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fullWidth: {
      width: '100%',
    },
    names: {
      fontSize: theme.typography.pxToRem(10),
      fontWeight: 500,
      paddingLeft: theme.spacing(1),
    },
    values: {
      fontSize: theme.typography.pxToRem(10),
      fontWeight: 350,
      paddingLeft: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
      textAlign: 'left',
    },
    icon: {
      width: 10,
      height: 10,
    },
    errorIcon: {
      color: theme.palette.error.dark,
    },
    statusMessage: {
      backgroundColor: '#F6E3E2',
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0.75),
      gap: theme.spacing(0.5),
    },
    statusMessageText: {
      fontWeight: 400,
      fontSize: pxToRem(10),
      color: '#5F2120',
    },
    articleLink: {
      fontFamily: 'Avenir',
      fontWeight: 400,
      fontSize: pxToRem(10),
      textDecoration: 'underline',
      textDecorationStyle: 'solid',
      textDecorationSkipInk: 'auto',
      textDecorationThickness: '0',
      textDecorationOffset: '0',
    },
    cardWrap: {
      textAlign: 'left',
    },
    summaryMsg: {
      paddingTop: theme.spacing(1.25),
      width: '100%',
    },
    riskTypography: {
      '& a': {
        color: '#5F2120',
        textDecoration: 'none',
        fontWeight: 500,
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    riskText: {
      fontSize: pxToRem(12),
    },
    alert: {
      padding: theme.spacing(0, 2),
      '& svg': {
        width: '18px',
        height: '18px',
        marginTop: theme.spacing(0.25),
      },
    },
  }),
);

type SummaryFieldKey = keyof SummaryStatus;
type SummaryFieldType = 'text' | 'date' | 'chip';

const SummaryInfo: React.FC<SummaryInfoProps> = ({ summary, isPassport, timezone }) => {
  const classes = useStyles();
  const summaryFields: { label: string; key: SummaryFieldKey; type: SummaryFieldType }[] = [
    { label: 'Name on ID', key: 'customerName', type: 'text' },
    { label: 'Transaction Date', key: 'transactionDate', type: 'date' },
    isPassport
      ? { label: 'Format Recognized', key: 'formatRecognized', type: 'chip' }
      : { label: 'Back of ID Barcode Recognized', key: 'barcodeRecognized', type: 'chip' },
    { label: 'Expired ID', key: 'expiredId', type: 'chip' },
    ...(summary?.selfieLive && summary?.selfieMatch
      ? [
          { label: 'Live Selfie', key: 'selfieLive' as SummaryFieldKey, type: 'chip' as SummaryFieldType },
          { label: 'Selfie Matches', key: 'selfieMatch' as SummaryFieldKey, type: 'chip' as SummaryFieldType },
        ]
      : [{ label: 'Selfie', key: 'selfie' as SummaryFieldKey, type: 'chip' as SummaryFieldType }]),
    {
      label: isPassport ? 'OCR-MRZ Match' : 'Printed Data on ID Matches Barcode Data',
      key: 'ocrBarcodeMatches',
      type: 'chip',
    },
    { label: 'Device Address', key: 'deviceAddress', type: 'chip' },
    { label: 'Live ID Document', key: 'documentLivenessFront', type: 'chip' },
    { label: 'Phone Number', key: 'phoneNumber', type: 'chip' },
  ];

  return (
    <div className={classes.fullWidth}>
      <Grid container spacing={1}>
        {summaryFields.map((field, index) => (
          <React.Fragment key={field.key}>
            <Grid item xs={index % 2 !== 0 ? 2 : 3}>
              <Typography className={classes.names}>{field.label}:</Typography>
            </Grid>
            <Grid item xs={index % 2 !== 0 ? 4 : 3} className={field.type === 'chip' ? classes.cardWrap : undefined}>
              {field.type === 'date' ? (
                <Typography className={classes.values}>
                  {formatDate(summary?.[field.key] as string, undefined, 'full', '', timezone)}
                </Typography>
              ) : field.type === 'chip' ? (
                <BasicChip status={summary?.[field.key] as string} />
              ) : (
                <Typography className={classes.values}>{summary?.[field.key] || 'N/A'}</Typography>
              )}
            </Grid>
          </React.Fragment>
        ))}
        {summary?.risks?.length > 0 && (
          <Alert
            severity="error"
            variant="standard"
            icon={<ErrorIcon className={classes.errorIcon} />}
            className={classes.alert}
          >
            <Box className={classes.riskTypography}>
              {summary?.risks.map((risk, index) => {
                return (
                  <Typography key={index} dangerouslySetInnerHTML={{ __html: risk }} className={classes.riskText} />
                );
              })}
            </Box>
          </Alert>
        )}
      </Grid>
    </div>
  );
};

export default SummaryInfo;
